import { MultiPolygon } from 'geojson';
import { interval, merge, Observable, Subject, ReplaySubject, Subscription } from 'rxjs';
import { tap, map, filter, takeWhile, debounceTime, takeUntil, throttleTime } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { SnTeam, SnTip, SnAlarm } from '@lib/api/entities';
import { SnUsersAPI, SnRecordingsAPI, SnSavedSearchAPI } from '@lib/api/services';
import { SnCurrentUser, SnCurrentOrganization } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { CustomActionMap } from '@lib/data-dog-rum/custom-action-map';
import { MapStoreService } from '@lib/geo/map';
import { FsLiveUpdateSwitchService } from '@lib/transport/live-update-switch';
import { SnSmartBadgeService } from '@lib/ui/badge';

import { SnLiveIncident, SnLiveUser } from '../models';
import { SnAlarmsTracking } from './alarms-tracking';
import { SnAlprTracking } from './alpr-tracking';
import { SnIncidentsTracking } from './incidents-tracking';
import { SnSentryTracking } from './sentry-tracking';
import { SnTipsTracking } from './tips-tracking';
import { SnUsersTracking } from './users-tracking';

export const ALPR_TTRA_METRICS_ALERT_RECEIVED_ACTION = 'alpr_ttra_metrics_alert_received';
export const ALPR_TTRA_METRICS_ALERT_SHOWN_ACTION = 'alpr_ttra_metrics_alert_shown';

@Injectable({
  providedIn: 'root',
})
export class SnLiveTrackingService implements OnDestroy {
  public liveUsersIsAvailable: boolean;
  public liveAlarmsIsAvailable: boolean;
  public liveTipsIsAvailable: boolean;
  public canViewLiveIncidents: boolean;

  public readonly usersTracking: SnUsersTracking;
  public readonly alarmsTracking: SnAlarmsTracking;
  public readonly alprTracking: SnAlprTracking;
  public readonly tipsTracking: SnTipsTracking;
  public readonly incidentsTracking: SnIncidentsTracking;
  public readonly sentryTracking: SnSentryTracking;

  private readonly _inited$ = new ReplaySubject<void>();
  private readonly _toastCreated$ = new Subject<{
    type: string;
    data?: SnAlarm | SnTip;
    action?: string;
  }>();
  private readonly _destroy$ = new Subject<void>();

  // New subjects for briefcam and vault export events
  private readonly _briefcamUploadComplete$ = new Subject<{ caseId: string; loginUrl: string }>();
  private readonly _briefcamUploadFailed$ = new Subject<void>();
  private readonly _vaultExportComplete$ = new Subject<void>();

  private _alarmParams: any = {};
  private _playSound$ = new Subject<void>();
  private _alarmSubscription: Subscription;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _liveUpdateSwitchService: FsLiveUpdateSwitchService,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _recordingsService: SnRecordingsAPI,
    public readonly _sentrySearchAPI: SnSavedSearchAPI,
    private readonly _ability: SnAbilityService,
    private readonly _mapStoreService: MapStoreService,
    private readonly _datadogService: FsDataDogRumService,
    smartBadge: SnSmartBadgeService,
  ) {
    this.usersTracking = new SnUsersTracking(this._usersAPI, smartBadge);
    this.alarmsTracking = new SnAlarmsTracking(smartBadge);
    this.alprTracking = new SnAlprTracking();
    this.tipsTracking = new SnTipsTracking(smartBadge);
    this.incidentsTracking = new SnIncidentsTracking(this._recordingsService, smartBadge);
    this.sentryTracking = new SnSentryTracking(smartBadge);
    this.canViewLiveIncidents = this._ability.can({
      action: 'can_view_live_stream',
      subject: 'incidents/recording',
    });
  }

  get inited$(): Observable<void> {
    return this._inited$.asObservable();
  }
  get toastCreated$(): Observable<{ type: string; data?: SnAlarm | SnTip; action?: string }> {
    return this._toastCreated$.asObservable();
  }

  get briefcamUploadComplete$(): Observable<{ caseId: string; loginUrl: string }> {
    return this._briefcamUploadComplete$.asObservable();
  }

  get briefcamUploadFailed$(): Observable<void> {
    return this._briefcamUploadFailed$.asObservable();
  }

  get vaultExportComplete$(): Observable<void> {
    return this._vaultExportComplete$.asObservable();
  }

  public ngOnDestroy(): void {
    this._inited$.complete();

    this._destroy$.next();
    this._destroy$.complete();

    this.usersTracking.destroy();
    this.alarmsTracking.destroy();
    this.tipsTracking.destroy();
    this.incidentsTracking.destroy();
  }

  public initLiveTracking(): void {
    this.liveUsersIsAvailable =
      this._ability.can(
        { subject: 'users/user', action: 'view_live_map' },
        { subject: 'users/user', action: 'view' },
      ) && !this._currentOrganization.additionalData?.disableLiveUserTracking;
    this.liveAlarmsIsAvailable = this._ability.can(
      { subject: 'users/user', action: 'view_live_map' },
      { subject: 'alarms/alarm', action: 'view' },
    );
    this.liveTipsIsAvailable = this._ability.can(
      { subject: 'users/user', action: 'view_live_map' },
      { subject: 'tips/tip', action: 'view' },
    );

    if (this.liveUsersIsAvailable || this.liveAlarmsIsAvailable || this.liveTipsIsAvailable) {
      this._liveUpdateSwitchService.initLiveTracking();
    }

    if (this.liveUsersIsAvailable || this.liveAlarmsIsAvailable) {
      this._initLiveTrackingSubscriptions();
    }

    if (this.liveUsersIsAvailable) {
      this._loadLiveUsers().subscribe(() => {
        this._initUserSubscriptions();
        this._inited$.next();
      });
    }

    if (this.liveAlarmsIsAvailable) {
      this._alarmParams = {
        ...this._alarmParams,
        message__not_in: this._mapStoreService.alarmsReasonsFilteredOff()?.join(',').toLowerCase(),
      };
      this._alarmSubscription = this._getAlarmCount()
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._initAlarmSubscriptions();
          this._inited$.next();
        });
    }

    if (this.liveTipsIsAvailable && this._currentOrganization.settings.allowSsss) {
      this._loadTips().subscribe(() => {
        this._initTipsSubscriptions();
        this._inited$.next();
      });
    }
  }

  public setAlarmParams(params: any): any {
    this._alarmParams = {
      ...this._alarmParams,
      ...params,
    };
    this._alarmSubscription = this._getAlarmCount().subscribe();
    this._mapStoreService.map.fire('idle');
  }

  public setToastData(toast: { type: string; data?: SnAlarm | SnTip; action?: string }): void {
    this._toastCreated$.next(toast);
  }

  public trackToast(data: SnAlarm): void {
    if (this._isAlpr(data)) {
      this.alprTracking.addAlpr(data);
      this._trackAlprTtra(ALPR_TTRA_METRICS_ALERT_SHOWN_ACTION, data);
    }
  }

  private _loadLiveUsers(): Observable<SnLiveUser[]> {
    return this._httpClient
      .get('/api/maps/live/organization-users/', { params: { pageSize: 'all' } })
      .pipe(
        tap(() => {
          this.usersTracking.clear();
          this.incidentsTracking.clear();
        }),
        map((response: any) => response.results.map((user) => new SnLiveUser(user))),
        tap((users) => {
          users.forEach((user) => {
            this.usersTracking.addUser(user, false);

            if (user.liveIncident) {
              this.incidentsTracking.addIncident(user.liveIncident, false);
            }
          });
        }),
      );
  }

  private _getAlarmCount(): Observable<number> {
    if (this._alarmSubscription) {
      this._alarmSubscription.unsubscribe();
    }
    return this._httpClient
      .get('/api/maps/live/alarms/', {
        params: {
          pageSize: '1',
          ...this._alarmParams,
        },
      })
      .pipe(
        map((response: any) => response.count),
        tap((count: number) => {
          this.alarmsTracking.setAlarmCount(count);
        }),
      );
  }

  private _loadTips(): Observable<SnTip[]> {
    return this._httpClient
      .get('/api/tips/', { params: { pageSize: 'all', isOpen: 'true', status: 'Open' } })
      .pipe(
        tap(() => this.tipsTracking.clear()),
        map((response: any) => response.results),
        tap((tips) => {
          tips.forEach((tip) => this.tipsTracking.addTip(tip, false));
        }),
      );
  }

  private _initUserSubscriptions(): void {
    const organization = this._currentOrganization;

    this._liveUpdateSwitchService
      .onReconnect(() => this._loadLiveUsers())
      .subscribe(() => this._inited$.next());

    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${organization.id}:users`,
      callback: ({ name, data }) => {
        switch (name) {
          case 'update:loginStatus':
            this._updateLoginStatus(data);
            break;

          case 'update:callSign':
            this._updateCallSignHandler(data);
            break;

          case 'update:onShiftStatus':
            this._updateLoginStatus(data);
            break;
        }
      },
    });

    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${organization.id}:geo`,
      callback: ({ name, data }) => {
        switch (name) {
          case 'update:location':
            this._updateLocationHandler(data);
            break;
        }
      },
    });

    if (this.canViewLiveIncidents) {
      this._liveUpdateSwitchService.subscribeToChannel({
        channel: `${organization.id}:recordings`,
        callback: ({ name, data, extras }) => {
          switch (name) {
            case 'create:recordingStarted':
              this._createRecordingStarted(data);
              break;

            case 'update:recordingStopped':
              this._updateRecordingStopped(data);
              break;

            case 'create:confidentialRecordingStarted':
              this._createRecordingStarted(data, true);
              break;

            case 'update:confidentialRecordingStopped':
              this._updateRecordingStopped(data, true);
              break;

            case 'update:briefcam:uploadStatus':
              this._handleBriefcamUploadStatus(data, extras);
              break;

            case 'update:recordingExportStatus':
              this._handleRecordingExportStatus(data, extras);
              break;
          }
        },
      });
    }
  }

  private _initAlarmSubscriptions(): void {
    this._playSound$.pipe(throttleTime(3000), takeUntil(this._destroy$)).subscribe(() => {
      const audio = new Audio('/assets/notifications/alarm.wav');
      audio.play();
    });

    const organization = this._currentOrganization;

    this._liveUpdateSwitchService
      .onReconnect(() => this._getAlarmCount())
      .subscribe(() => this._inited$.next());

    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${organization.id}:alarms`,
      callback: ({ name, data }) => {
        if (this._isAlpr(data)) {
          this._trackAlprTtra(ALPR_TTRA_METRICS_ALERT_RECEIVED_ACTION, data);
        }
        if (this._alarmReasonIsFilteredOut(data) || this._alarmTypeIsFilteredOut(data)) {
          return;
        }
        switch (name) {
          case 'create:alarm':
            this._alarmCreated(data);
            break;

          case 'update:alarm:acked':
            this._alarmUpdated(data);
            break;

          case 'update:alarm:closed':
            this._alarmClosed(data);
            break;

          case 'update:alarm:closedAll':
            this.alarmsTracking.setAlarmCount(0);
            break;
        }
      },
    });
  }

  private _initTipsSubscriptions(): void {
    const organization = this._currentOrganization;

    this._liveUpdateSwitchService
      .onReconnect(() => this._loadTips())
      .subscribe(() => this._inited$.next());

    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${organization.id}:tips`,
      callback: ({ name, data }) => {
        switch (name) {
          case 'tipCreated':
            this._tipCreated(data);
            break;

          case 'tipUpdated':
            this._tipUpdated(data);
            break;

          case 'tipDeleted':
            this._tipClosed(data);
            break;
        }
      },
    });
  }

  private _initLiveTrackingSubscriptions(): void {
    merge<SnLiveUser | SnLiveIncident>(
      this.inited$,
      this.usersTracking.userLoggedIn$,
      this.usersTracking.userLoggedOut$,
      this.usersTracking.userCallSignChanged$,
      this.incidentsTracking.incidentStarted$,
      this.incidentsTracking.incidentStopped$,
    )
      .pipe(debounceTime(300))
      .subscribe({
        next: () => this.usersTracking.sortUsersByPriority(),
      });
  }

  private _updateLoginStatus(data: any): void {
    if (data.mobileLoggedIn && data.settings.isOnShift) {
      const user = new SnLiveUser(data);
      this.usersTracking.addUser(user);
    } else {
      this.usersTracking.removeUser(data.id);
    }
  }

  private _updateCallSignHandler(data: any): void {
    const team = new SnTeam(data.team);
    this.usersTracking.updateUserCallSign(data.id, data.callSign, team);
  }

  private _updateLocationHandler(data: any): void {
    this.usersTracking.updateUserLocation(data.id, data.geometry, data.altitude, data.speed);
  }

  private _createRecordingStarted(data: any, confidential?: boolean): void {
    if (confidential) {
      const isAllowed = data.team?.id === this._currentUser.team?.id;
      if (!isAllowed) {
        return;
      }
    }
    this.usersTracking.getUser(data.user.id).subscribe({
      next: (user) => {
        const incident = new SnLiveIncident(data);
        user.startIncident(incident);
        this.incidentsTracking.addIncident(incident);
        this._addStreamNotification(incident);
      },
    });
  }

  private _updateRecordingStopped(data: any, confidential?: boolean): void {
    if (confidential) {
      const isAllowed = data.team?.id === this._currentUser.team?.id;
      if (!isAllowed) {
        return;
      }
    }
    this.usersTracking.getUser(data.user.id).subscribe({
      next: (user) => {
        user.stopIncident();
        this.incidentsTracking.removeIncident(data.id);
      },
    });
  }

  private _addStreamNotification(incident: SnLiveIncident): void {
    const userSettings = this._currentUser.settings;

    if (userSettings.streamNotificationSound) {
      const audio = new Audio('/assets/notifications/stream.mp3');

      interval(1000)
        .pipe(
          filter(() => !this.incidentsTracking.hasPlayableIncident()),
          takeWhile((value) => {
            return (
              (incident.user.isLive &&
                !incident.isPlayed &&
                userSettings.notificationSoundPlayCount === 0) ||
              userSettings.notificationSoundPlayCount > value
            );
          }),
        )
        .subscribe({
          next: () => audio.play(),
        });
    }
  }

  private _isAlpr(alarm: SnAlarm): boolean {
    if (alarm && alarm?.type) {
      return alarm.type.toLowerCase().indexOf('lpr') > -1;
    }
  }

  private _alarmCreated(data: any): void {
    const message = data.message?.toString()?.trim()?.toLowerCase() || '';

    if (!this._isInBoundaries(data)) {
      return;
    }

    this.alarmsTracking.addAlarm(data, true);
    if (this._mapStoreService.isInnerLayerSelected('alarms')) {
      if (this._currentUser.settings.layerSetting[data.type?.toLowerCase()]?.muteSound) {
        return;
      }
      if (this._currentUser.settings.layerSetting[message]?.muteSound) {
        return;
      }
      this._playSound$.next();
    }
  }

  private _isInBoundaries(alarm: SnAlarm): boolean {
    if (this._currentUser.alertBoundaryGeoJson) {
      return this._isAlertInBoundary(alarm);
    }
    return this._isGlobalAlertBoundary(alarm);
  }

  private _isAlertInBoundary(data: any): boolean {
    const boundary: MultiPolygon = this._currentUser.alertBoundaryGeoJson;
    if (
      (data.lat === null || data.lat === undefined) &&
      (data.lon === null || data.lon === undefined)
    ) {
      return false;
    }
    const point = new google.maps.LatLng(data.lat, data.lon);
    if (!boundary?.coordinates?.length) {
      return true;
    }
    if (boundary && point) {
      let contains = false;
      boundary.coordinates.forEach((polygonCoords) => {
        polygonCoords.forEach((polygonPoints) => {
          const paths = polygonPoints.map((position) => {
            return new google.maps.LatLng(position[1], position[0]);
          });
          const polygon = new google.maps.Polygon({ paths });
          if (google.maps.geometry.poly.containsLocation(point, polygon)) {
            contains = true;
          }
        });
      });
      return contains;
    }
    return false;
  }

  private _isGlobalAlertBoundary(data: any): boolean {
    const boundary: MultiPolygon = this._currentOrganization.alertsBoundary;
    if (
      (data.lat === null || data.lat === undefined) &&
      (data.lon === null || data.lon === undefined)
    ) {
      return false;
    }
    const point = new google.maps.LatLng(data.lat, data.lon);
    if (!boundary?.coordinates?.length) {
      return true;
    }
    if (boundary && point) {
      let contains = false;
      boundary.coordinates.forEach((polygonCoords) => {
        polygonCoords.forEach((polygonPoints) => {
          const paths = polygonPoints.map((position) => {
            return new google.maps.LatLng(position[1], position[0]);
          });
          const polygon = new google.maps.Polygon({ paths });
          if (google.maps.geometry.poly.containsLocation(point, polygon)) {
            contains = true;
          }
        });
      });
      return contains;
    }
    return false;
  }

  private _alarmUpdated(data: any): void {
    this.alarmsTracking.updateAlarm(data);
  }

  private _alarmClosed(data: any): void {
    this.alarmsTracking.removeAlarm(data);
    if (this._isAlpr(data)) {
      this.alprTracking.removeAlpr(data);
    }
  }

  private _alarmReasonIsFilteredOut(data: any): boolean {
    const alertType = data?.type?.toString()?.trim()?.toLowerCase() || '';
    if (!data.message || alertType !== 'lpr') {
      return false;
    }
    const message = data.message?.toString()?.trim()?.toLowerCase();
    const enabledAlertReasons = this._currentUser.settings?.mapSetting?.enabledAlertReasons || [];
    return enabledAlertReasons?.includes(message) ? false : true;
  }

  private _alarmTypeIsFilteredOut(data: any): boolean {
    if (!data.type) {
      return false;
    }
    const alertType = data.message?.toString()?.trim()?.toLowerCase();
    const disabledAlertTypes = this._currentUser.settings?.mapSetting?.disabledAlertTypes || [];
    return disabledAlertTypes.includes(alertType) ? true : false;
  }

  private _tipCreated(data: any): void {
    this.tipsTracking.addTip(data);
    if (this._currentUser.settings.layerSetting['tips']?.muteSound) {
      return;
    }
    const audio = new Audio('/assets/notifications/tip.mp3');
    audio.play();
  }

  private _tipUpdated(data: any): void {
    if (data.status === 'Closed') {
      this._tipClosed(data);
      return;
    }
    this.tipsTracking.updateTip(data.id, data);
  }

  private _tipClosed(data: any): void {
    this.tipsTracking.removeTip(data.id);
  }

  private _trackAlprTtra(
    action: keyof CustomActionMap,
    data: {
      id: string;
      type: string;
      details?: { eventTimestamp?: string; provider?: string };
    },
  ): void {
    const receivedTime = new Date();
    const eventTime = data.details?.eventTimestamp;
    if (!eventTime) return;

    const delta = receivedTime.getTime() - new Date(eventTime).getTime();

    this._datadogService.addAction(action, {
      alert_id: data.id,
      alert_type: data.details?.provider ?? '',
      received_time: receivedTime.toISOString(),
      event_time: eventTime,
      delta_seconds: delta / 1000,
    });
  }

  private _handleBriefcamUploadStatus(data: any, extras: any): void {
    const isCurrentUser = this._currentUser.id === extras?.headers?.user_id;
    if (!isCurrentUser) {
      return;
    }

    // Use string literals as enum values since we can't import the enums from dashboard app
    if (data.briefcamUploadStatus === 'FINISHED') {
      this._briefcamUploadComplete$.next({
        caseId: extras.headers.case_id,
        loginUrl: '', // Will be set by the consuming component
      });
    } else if (data.briefcamUploadStatus === 'ERROR') {
      this._briefcamUploadFailed$.next();
    }
  }

  private _handleRecordingExportStatus(data: any, extras: any): void {
    const isCurrentUser = this._currentUser.id === extras?.headers?.user_id;
    if (!isCurrentUser) {
      return;
    }

    if (data.exportStatus === 'FINISHED') {
      this._vaultExportComplete$.next();
    }
  }
}
